import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useSnackbar } from "notistack";
import {
  getPaymentMethodAsync,
  savePaymentMethodAsync,
  deletePaymentMethodAsync,
} from "../infrastructure/httpclient";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from '../context/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ onCardSaved }) => {
  const CARD_ELEMENT_OPTIONS = {
    hidePostalCode: true,
    style: {
      base: {
        color: "#fff",              // input text color
        fontSize: "16px",
        "::placeholder": {
          color: "#bbb",           // placeholder text color
        },
      },
      invalid: {
        color: "#ff4d4f",          // error text color
      },
    },
  };
  const { user, token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSaveCard = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        email: user.email
      },
    });

    if (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } else {
      try {
        // Send paymentMethod.id to backend
        await savePaymentMethodAsync(token, {
          paymentmethod_id: paymentMethod.id
        });

        enqueueSnackbar("Card saved successfully!", { variant: "success" });
        onCardSaved(paymentMethod);
      } catch (err) {
        enqueueSnackbar("Failed to save payment method.", { variant: "error" });
      }
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSaveCard}>
      <Box
        sx={{
          border: "1px solid white",   // white border
          borderRadius: "8px",
          padding: "12px 16px",        // makes input area bigger
          mb: 2,
        }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>

      <Button
        variant="contained"
        color="primary"
        type="submit"
        disabled={!stripe || isSubmitting}
        sx={{ mt: 2 }}
      >
        {isSubmitting ? <CircularProgress size={24} /> : "Save Card"}
      </Button>
    </form>
  );
};

const PaymentsPage = () => {
  const { user, token } = useAuth();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    const fetchCard = async () => {
      setIsLoading(true);
      try {
        const response = await getPaymentMethodAsync(token);
        if (response?.data) {
          const viewModel = {
            brand: response.data.card_brand,
            last4: response.data.card_last4,
            exp_month: response.data.card_exp_month,
            exp_year: response.data.card_exp_year,
          };

          setPaymentInfo(viewModel);

          const expiry = new Date(response.data.card_exp_year, response.data.card_exp_month, 0);
          const today = new Date();
          setIsExpired(expiry < today);
        } else {
          setPaymentInfo(null);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          enqueueSnackbar("Failed to fetch payment method.", { variant: "info" });
        }
      }
      setIsLoading(false);
    };

    fetchCard();
  }, [enqueueSnackbar, token]);


  const handleRemoveCard = async () => {
    setDeleteConfirmOpen(false);
    setIsSubmitting(true);
    try {
      await deletePaymentMethodAsync(token);
      setPaymentInfo(null);
      setIsExpired(false);
      enqueueSnackbar("Card removed successfully!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to remove payment method.", { variant: "error" });
    }
    setIsSubmitting(false);
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
  };

  if (!user) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">
          You must be logged in to view this page.
        </Typography>
      </Container>
    );
  }


  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Payment Method
          </Typography>

          {paymentInfo ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                <CreditCardIcon sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {paymentInfo.brand?.toUpperCase()} **** {paymentInfo.last4}
                  </Typography>
                  <Typography color={isExpired ? "error" : "text.secondary"}>
                    Expires: {paymentInfo.exp_month}/{paymentInfo.exp_year}{" "}
                    {isExpired && "(Expired)"}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteClick}
                sx={{ mt: 2 }}
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Remove Card"}
              </Button>
            </Box>
          ) : (
            <PaymentForm
              onCardSaved={(paymentMethod) =>
                setPaymentInfo({
                  brand: paymentMethod.card.brand,
                  last4: paymentMethod.card.last4,
                  exp_month: paymentMethod.card.exp_month,
                  exp_year: paymentMethod.card.exp_year,
                })
              }
            />
          )}
        </Paper>
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteConfirmClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Confirm Card Deletion"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this card? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
            <Button onClick={handleRemoveCard} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Elements>
  );
};

export default PaymentsPage;
